import type { AnyFormApi } from '@tanstack/react-form';
import type { SubmitEventHandler } from 'react';
import type { ZodType } from 'zod';
import type { ContentTypeFormProperty } from '../helpers/parseProperty';

import { use, useCallback, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { batch } from '@tanstack/store';
import slug from 'slug';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import CancelButton from '../../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../../components/Button/predefined/SaveButton/SaveButton';

import ContentTypeFormContext from '../../../contexts/ContentTypeFormContext';
import ListPropertiesContext from '../../../contexts/ListPropertiesContext';
import {
  ModalInstanceContext,
  useModals,
} from '../../../contexts/ModalContext';

import { ArrowLeftThinIcon } from '../../../images/shapes';

import { useAppForm } from '../../../form-hooks/form';
import ListProperties from '../../ContentTypeForm/ListProperties/ListProperties';
import { parseInputType } from '../helpers/parseProperty';
import { EDITABLE } from '../helpers/propertiesFields';
import PropertiesSettings from '../PropertiesSettings/PropertiesSettings';
import PropertyCard from '../PropertyCard/PropertyCard';

export const TRANSLATIONS_KEYS: Record<
  string,
  { key: string; tooltipKey?: string }
> = {
  text: { key: 'Text' },
  textarea: { key: 'Textarea' },
  textMarkdown: { key: 'Markdown' },
  email: { key: 'Email' },
  richtext: { key: 'Richtext' },
  number: { key: 'Number' },
  radio: { key: 'Radio' },
  checkbox: { key: 'Checkbox' },
  select: { key: 'Select' },
  datasource: { key: 'Relation' },
  object: { key: 'List', tooltipKey: 'List' },
  simpleList: { key: 'SimpleList' },
  geo: { key: 'Geo' },
  media: { key: 'Media' },
  dateTime: { key: 'DateTime' },
  block: { key: 'Block' },
};

interface PropertyModalFormContentProps {
  /**
   * On submit callback
   */
  onSubmit: ({
    value,
    formApi,
  }: {
    value: ContentTypeFormProperty;
    formApi: AnyFormApi;
  }) => void;
  /**
   * Property object to edit
   */
  property?: ContentTypeFormProperty;
  /**
   * Validation schema for form
   */
  validationSchema?: ZodType;
  /**
   * Name of the parent field
   */
  parentInputType?: string;
  /**
   * Test id for property modal form
   */
  testId?: string;
}

const propertyKeyRegex =
  /^(id|_id|_index|_type|_source|_score|_routing|_version|_shards|_geo_distance|objectType|internal)$/;

const PropertyModalFormContent = ({
  onSubmit,
  property,
  validationSchema,
  testId = '',
  parentInputType,
}: PropertyModalFormContentProps) => {
  const formId = useId();
  const { t } = useTranslation();
  const modal = useModals();
  const modalInstance = use(ModalInstanceContext);
  const { setDirty, propertiesErrors } = use(ContentTypeFormContext);
  const { isNestedList } = use(ListPropertiesContext);

  const propertyErrors = useMemo(
    () => (property?.key ? propertiesErrors[property.key] : undefined),
    [propertiesErrors, property?.key],
  );

  const onCancel = useCallback(
    () => modalInstance.resolve?.(null),
    [modalInstance],
  );

  const initialValues = useMemo(
    () =>
      property
        ? JSON.parse(JSON.stringify(property))
        : {
            key: '',
            config: { label: '', helpText: '', unique: false },
            schema: { type: '' },
            required: false,
          },
    [property],
  );

  const form = useAppForm({
    defaultValues: initialValues as ContentTypeFormProperty,
    onSubmit: onSubmit,
    validators: { onChange: validationSchema },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const handleTypeChange = useCallback(async () => {
    if (property) {
      const result = await modal.confirmation(
        t('PropertyForm.ChangeTypeConfirm'),
      );
      if (!result) return;
    }
    form.setFieldValue('schema.type', '');
    form.setFieldValue('config.inputType', '');
    setDirty(true);
  }, [property, form, setDirty, modal, t]);

  const onPropertyCardClick = useCallback(
    async (inputType: string) => {
      if (EDITABLE.indexOf(inputType) < 0) {
        alert('Not implemented');
        return;
      }

      const newProperty = parseInputType(inputType, form.state.values);

      batch(() => {
        Object.keys(newProperty).forEach((key) => {
          form.setFieldValue(
            key as keyof ContentTypeFormProperty,
            newProperty[key as keyof typeof newProperty],
            { dontRunListeners: true, dontValidate: true },
          );
        });
      });

      setDirty(true);

      /**
       * For some weird reason, two validations are required.
       * Validate all fields is not validating input type field and validate - schema type field
       */
      form.validateAllFields('change');
      form.validate('change');
    },
    [form, setDirty],
  );

  const renderPropertySettings = useCallback(
    (inputType: string, values: ContentTypeFormProperty) => {
      const isMedia =
        values.config?.validation?.relationContenttype === '_media';
      return (
        <>
          <div>
            <div className="text-sm text-slate-400 mb-1">
              {t('PropertyForm.PropertyType')}
            </div>
            <PropertyCard
              inputType={isMedia ? 'media' : inputType}
              propertyErrors={propertyErrors}
              testId={testId}
            />
            <Button
              onClick={handleTypeChange}
              iconImage={
                <ArrowLeftThinIcon className="w-2.5 text-slate-700 dark:text-blue stroke-2" />
              }
              buttonColor="borderless"
              iconPosition="start"
              additionalClasses="text-sm mt-4"
              {...getTestProps(testId, 'change-type', 'testId')}
            >
              {t('PropertyForm.ChangePropertyType')}
            </Button>

            {inputType === 'object' && (
              <ListProperties
                form={form}
                fields={{
                  key: 'key',
                  required: 'required',
                  config: 'config',
                  schema: 'schema',
                }}
                listName={values.key}
                listPropertiesItems={values.config.items}
                listSchemaItems={values.schema.items}
                parentInputType={
                  parentInputType
                    ? `${parentInputType}_${inputType}`
                    : inputType
                }
                isOpen={true}
                additionalClasses="px-0"
                {...getTestProps(testId, `${values.key}-properties`, 'testId')}
              />
            )}
          </div>

          <PropertiesSettings
            form={form}
            inputType={inputType}
            isMedia={isMedia}
            testId={testId}
          />
        </>
      );
    },
    [t, testId, handleTypeChange, form, parentInputType, propertyErrors],
  );

  useEffect(() => {
    const propertyFormErrors = propertyErrors?.form || {};
    if (Object.keys(propertyFormErrors).length > 0) {
      Object.entries(propertyFormErrors).forEach(([key, error]) => {
        form.setFieldMeta(key as keyof ContentTypeFormProperty, (prev) => ({
          ...prev,
          errorMap: { onSubmit: error },
          isTouched: true,
        }));
      });
    }
  }, [form, propertyErrors]);

  return (
    <form.AppForm>
      <form
        id={formId}
        className="space-y-2 md:space-y-4 pb-5"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="flex flex-col sm:flex-row gap-2 md:gap-10">
          <form.AppField
            name="config.label"
            listeners={{
              onBlur: ({ value, fieldApi }) => {
                if (fieldApi.form.getFieldValue('key')) return;
                fieldApi.form.setFieldValue('key', slug(value, '_'));
                fieldApi.form.validateField('key', 'change');
              },
            }}
          >
            {(field) => (
              <field.Input
                label={t('PropertyForm.Label')}
                required
                {...getTestProps(testId, 'label', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField
            name="key"
            validators={{
              onBlur: ({ value }) => {
                if (propertyKeyRegex.test(value)) {
                  return t('ContentTypeForm.Errors.SystemNameKey');
                }
              },
            }}
          >
            {(field) => (
              <field.Input
                label={t('PropertyForm.Key')}
                required
                {...getTestProps(testId, 'key', 'testId')}
              />
            )}
          </form.AppField>
        </div>

        {/**
         * There are problems that main validation has no actual data of this field,
         * due to the fact that it is set programmatically.
         */}
        <form.AppField
          name="schema.type"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('PropertyForm.Errors.Type');
            },
          }}
        >
          {(subField) => (
            <subField.ErrorField
              {...getTestProps(testId, 'schema-type-error', 'testId')}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={({ values }) => values}>
          {(values) => {
            const inputType = values.config?.inputType;
            return inputType ? (
              renderPropertySettings(inputType, values)
            ) : (
              <div
                className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 pb-5"
                {...getTestProps(testId, 'property-cards')}
              >
                {Object.keys(TRANSLATIONS_KEYS).map((inputType) => {
                  if (isNestedList && inputType === 'object') return null;
                  return (
                    <PropertyCard
                      key={inputType}
                      inputType={inputType}
                      onClick={onPropertyCardClick}
                      propertyErrors={propertyErrors}
                      testId={testId}
                    />
                  );
                })}
              </div>
            );
          }}
        </form.Subscribe>

        <div
          className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
                    z-10 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
        >
          <CancelButton
            onClick={onCancel}
            {...getTestProps(testId, 'cancel', 'testId')}
          />
          <SaveButton
            form={formId}
            {...getTestProps(testId, 'save', 'testId')}
          />
        </div>
      </form>

      <form.ValidationToastHandler hideToast />
    </form.AppForm>
  );
};

export default PropertyModalFormContent;
